import { useEffect } from "react";
import { AlertTriangle, Check, Info, X, XCircle } from "lucide-react";
import type { Toast as ToastData } from "@/types";
import { useAppStore } from "@/stores";
import { cn } from "@/utils";
import { Portal } from "./Portal";

/**
 * Toast system.
 *
 * Mount {@link Toaster} once near the root. Emit notifications from anywhere:
 *
 *   useAppStore.getState().addToast({ title: "Saved", variant: "success" });
 *
 * Toasts auto-dismiss after their `duration` (0 = sticky) and stack in the
 * bottom-right, above all other layers.
 */
const icons = {
  default: Info,
  success: Check,
  warning: AlertTriangle,
  error: XCircle,
} as const;

function ToastItem({ toast }: { toast: ToastData }) {
  const dismiss = useAppStore((s) => s.dismissToast);
  const Icon = icons[toast.variant];

  useEffect(() => {
    if (toast.duration <= 0) return;
    const timer = setTimeout(() => dismiss(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "pointer-events-auto flex w-80 items-start gap-3 rounded-lg border border-border-strong",
        "bg-bg-overlay px-3 py-3 shadow-overlay animate-slide-up",
      )}
    >
      <Icon size={15} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-base font-medium text-text-primary">{toast.title}</p>
        {toast.description && (
          <p className="text-sm text-text-secondary">{toast.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label="Dismiss"
        className="-mr-1 -mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
      >
        <X size={13} />
      </button>
    </div>
  );
}

export function Toaster() {
  const toasts = useAppStore((s) => s.toasts);
  if (toasts.length === 0) return null;

  return (
    <Portal>
      <div className="pointer-events-none fixed bottom-4 right-4 z-toast flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </div>
    </Portal>
  );
}
